import React, { createContext, useContext, useEffect, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useDeviceInfo, useLogger, useSelector } from '~Hooks';
import { datadogLogs } from '@datadog/browser-logs';

const TIME_QUERY = gql`
  query CurrentTime {
    currentTime
  }
`;

const NUM_REQUESTS = 5;
const ONE_HOUR = 60 * 60 * 1000;

// Static TimeManager object for global access
const TimeManager = {
  offset: 0,

  /**
   * This function returns the current time adjusted by the offset.
   * It can be accessed globally without using React hooks.
   */
  now: () => {
    return new Date(Date.now() + TimeManager.offset);
  },
};

// Create the TimeManager context
const TimeManagerContext = createContext({
  offset: 0,
  now: TimeManager.now,
});

export const useTimeManager = () => useContext(TimeManagerContext);

const TimeManagerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const logger = useLogger('time-manager');
  const [syncedTime, setSyncedTime] = useState<Date | null>(null);
  const { refetch } = useQuery(TIME_QUERY, { skip: true });
  const deviceInfo = useDeviceInfo();
  const [updateIndex, setUpdateIndex] = useState(0);

  const country = useSelector((state) => state.country);

  // Moved usePreciseSyncedTime logic here
  useEffect(() => {
    logger.info('Effect. ID: + ' + updateIndex);

    /** Time sync is enabled for all devices in Spain due to escalated 2nd-line related to table-events */
    if ((!deviceInfo || deviceInfo.systemName !== 'Android') && country !== 'ES') {
      logger.info('Skipping time synchronization');
      return;
    }

    async function fetchAndSyncTime() {
      const timeDifferences: number[] = [];
      logger.info('Starting time synchronization');

      // Sleep for 5 seconds to ensure we dont have a bunch of other
      // startup requests that could interfere with the time sync.
      await new Promise((resolve) => setTimeout(resolve, 5000));

      try {
        for (let i = 0; i < NUM_REQUESTS; i++) {
          const start = Date.now();
          const result = await refetch();
          const end = Date.now();

          if (!result.data || !result.data.currentTime) {
            logger.error('Failed to fetch time from server');
            continue;
          }

          const serverTime = new Date(result.data.currentTime).getTime();
          const roundTripTime = end - start;
          const estimatedServerTime = serverTime + roundTripTime / 2;
          const localTime = (start + end) / 2;

          timeDifferences.push(estimatedServerTime - localTime);
          logger.info('Round trip time: ' + roundTripTime);
        }
      } catch (e) {
        logger.error((e as Error).message);
        return;
      }

      const averageTimeDifference =
        timeDifferences.reduce((a, b) => a + b, 0) / timeDifferences.length;

      setSyncedTime(new Date(Date.now() + averageTimeDifference));
    }

    fetchAndSyncTime();
  }, [updateIndex, deviceInfo]);

  // Update time every hour
  useEffect(() => {
    const interval = setInterval(() => {
      logger.info('Updating time');
      setUpdateIndex((prev) => prev + 1);
    }, ONE_HOUR);

    return () => clearInterval(interval);
  }, []);

  // Set the offset when the synced time is available
  useEffect(() => {
    if (!syncedTime) {
      logger.info('Skipping time offset - no synced time');
      return;
    }

    if (!deviceInfo || deviceInfo.systemName !== 'Android') {
      logger.info('Skipping time offset - not Android');
      return;
    }

    logger.info('Setting time offset');

    const localTime = Date.now();
    const serverTime = syncedTime.getTime();
    const timeDifference = serverTime - localTime;

    datadogLogs.addLoggerGlobalContext('time-synchronization', {
      localTime: new Date().toISOString(),
      serverTime: syncedTime.toISOString(),
      timeDifference,
      adjusted: new Date(localTime + timeDifference).toISOString(),
    });

    // Set both the context and static TimeManager offset
    TimeManager.offset = timeDifference;
  }, [deviceInfo, syncedTime]);

  return (
    <TimeManagerContext.Provider value={{ offset: TimeManager.offset, now: TimeManager.now }}>
      {children}
    </TimeManagerContext.Provider>
  );
};

// Export the static TimeManager for global access
export default TimeManager;
export { TimeManagerProvider };
